<script setup>
import { ref, computed } from 'vue'
import { Head, Link } from '@inertiajs/vue3'
import { Search, Plus, Minus, MessageSquare } from 'lucide-vue-next'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageHero from '@/Components/Marketing/PageHero.vue'
import { Button } from '@/Components/ui/button'
import { tutors } from '@/routes/index'

/**
 * Every question, grouped, with a search that filters as you type.
 *
 * The answers are written for the product as it exists today. Nothing here
 * describes Bindle Live as something a parent can buy, because they cannot.
 *
 * Each group is an id target, so the anchors in the side rail — and any link
 * sent in a support reply — land in the right place under the sticky header.
 */
const props = defineProps({
  groups: { type: Array, default: () => [] },
  figures: { type: Object, default: () => ({}) },
})

const query = ref('')
const open = ref(new Set(['0-0']))

const toggle = (key) => {
  const next = new Set(open.value)
  next.has(key) ? next.delete(key) : next.add(key)
  open.value = next
}

const slug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

/**
 * Every word has to appear, not the whole phrase.
 *
 * The filter matched the query as one contiguous string, so a question typed the
 * way anyone would ask it — "how lessons run" — found nothing, even though the
 * "Lessons" group answers exactly that. No entry contains that run of words in
 * that order, and none ever will.
 *
 * Words are matched against the question and the answer together, so the terms
 * may be spread across both.
 */
const matchCount = (item, words) => {
  const haystack = `${item.q} ${item.a}`.toLowerCase()

  return words.filter((word) => haystack.includes(word)).length
}

/**
 * Ordered by how much of the query each answer accounts for, so a common word
 * like "how" cannot float unrelated questions above the ones that match the
 * rest of the phrase. Groups follow their best answer.
 */
const groupsMatching = (words, minimum) =>
  props.groups
    .map((group) => {
      const items = group.items
        .map((item) => ({ item, score: matchCount(item, words) }))
        .filter(({ score }) => score >= minimum(words.length))
        .sort((a, b) => b.score - a.score)

      return {
        ...group,
        items: items.map(({ item }) => item),
        score: items[0]?.score ?? 0,
      }
    })
    .filter((group) => group.items.length > 0)
    .sort((a, b) => b.score - a.score)

/**
 * Falls back to the closest questions rather than showing nothing.
 *
 * Two things were wrong. The query was matched as one contiguous string, so a
 * question typed the way anyone would ask it — "how lessons run" — found
 * nothing. Matching each word instead fixes that, but not this example: the FAQ
 * answers it under "How do lessons happen?", and the word "run" appears nowhere
 * on the page. No keyword search bridges a synonym.
 *
 * So when nothing matches every word, anything matching one is shown. "Lessons"
 * still lands on the lessons section, which is where the answer is, and a page
 * of near misses beats a page of nothing.
 */
const isApproximate = ref(false)

const filteredGroups = computed(() => {
  const words = query.value.trim().toLowerCase().split(/\s+/).filter(Boolean)

  if (!words.length) {
    isApproximate.value = false

    return props.groups
  }

  const exact = groupsMatching(words, (total) => total)

  if (exact.length || words.length === 1) {
    isApproximate.value = false

    return exact
  }

  const approximate = groupsMatching(words, () => 1)
  isApproximate.value = approximate.length > 0

  return approximate
})

const resultsRef = ref(null)

/**
 * Pressing Enter takes you to the answers.
 *
 * The list of questions starts below the fold, so searching moved nothing the
 * reader could see and the search looked as though it had done nothing. The
 * count under the box answers immediately; this is for the reflex of typing and
 * hitting Enter.
 */
const showResults = () => {
  resultsRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const resultCount = computed(() =>
  filteredGroups.value.reduce((total, group) => total + group.items.length, 0)
)
</script>

<template>
  <Head>
    <title>Frequently asked questions — Bindle</title>
    <meta name="description" content="How Bindle selects tutors, how lessons and billing work, and what parents can see." />
  </Head>

  <AppLayout>
    <PageHero
      eyebrow="Help"
      title="Frequently asked questions"
      lead="Choosing a tutor, how lessons run, what it costs and how we keep it safe. If the answer you need is not here, the Help button reaches a person."
    >
      <div class="relative mt-8 w-full max-w-md">
        <Search class="pointer-events-none absolute left-3 top-1/2 size-4.5 -translate-y-1/2 text-subtle" />
        <input
          v-model="query"
          type="search"
          placeholder="Search questions"
          aria-label="Search questions"
          class="h-12 w-full rounded-md border border-line-strong bg-surface pl-10 pr-3 text-[15px] text-ink placeholder:text-subtle focus:border-brand focus:outline-none focus:ring-3 focus:ring-brand/20"
          @keyup.enter="showResults"
        />
      </div>
      <p v-if="query" class="mt-3 text-sm text-muted">
        <template v-if="isApproximate">
          Nothing matches “{{ query }}” exactly. Showing the
          {{ resultCount }} closest {{ resultCount === 1 ? 'answer' : 'answers' }}.
        </template>
        <template v-else>
          {{ resultCount }} {{ resultCount === 1 ? 'answer' : 'answers' }} for “{{ query }}”
        </template>
        <button
          v-if="resultCount"
          type="button"
          class="ml-1 font-semibold text-brand hover:underline"
          @click="showResults"
        >Jump to them</button>
      </p>
    </PageHero>

    <section ref="resultsRef" class="section scroll-mt-24">
      <div class="container-page grid gap-10 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-14">
        <!-- Side rail -->
        <nav class="hidden lg:block" aria-label="Question categories">
          <div class="sticky top-28">
            <p class="text-xs font-extrabold uppercase tracking-[0.14em] text-subtle">On this page</p>
            <ul class="mt-4 space-y-1">
              <li v-for="group in groups" :key="group.title">
                <a
                  :href="`#${slug(group.title)}`"
                  class="block rounded-md px-3 py-2 text-sm font-semibold text-muted transition-colors hover:bg-surface-2 hover:text-ink"
                >{{ group.title }}</a>
              </li>
            </ul>
          </div>
        </nav>

        <div>
          <div v-if="filteredGroups.length" class="space-y-12">
            <section v-for="(group, groupIndex) in filteredGroups" :id="slug(group.title)" :key="group.title" class="scroll-mt-28">
              <h2 class="text-2xl">{{ group.title }}</h2>
              <div class="mt-4 overflow-hidden rounded-xl border border-line bg-surface">
                <div
                  v-for="(item, index) in group.items"
                  :key="item.q"
                  class="border-b border-line last:border-0"
                >
                  <button
                    type="button"
                    class="flex w-full items-center justify-between gap-5 px-5 py-4 text-left text-[15px] font-semibold text-ink transition-colors hover:bg-surface-2"
                    :aria-expanded="open.has(`${groupIndex}-${index}`)"
                    @click="toggle(`${groupIndex}-${index}`)"
                  >
                    {{ item.q }}
                    <Minus v-if="open.has(`${groupIndex}-${index}`)" class="size-4.5 shrink-0 text-brand" />
                    <Plus v-else class="size-4.5 shrink-0 text-brand" />
                  </button>
                  <p
                    v-show="open.has(`${groupIndex}-${index}`)"
                    class="px-5 pb-5 text-[15px] leading-relaxed text-muted"
                  >{{ item.a }}</p>
                </div>
              </div>
            </section>
          </div>

          <p v-else class="rounded-xl border border-dashed border-line-strong p-10 text-center text-muted">
            Nothing matches “{{ query }}”. Try a shorter phrase, or use the Help button to ask us directly.
          </p>

          <!-- Still stuck -->
          <!-- No call to action here. Someone reading the FAQ has a question,
               not a shortlist; sending them to the listing answers nothing. -->
          <div class="panel-brand mt-12 flex gap-4 p-7">
            <MessageSquare class="mt-0.5 size-6 shrink-0" />
            <div>
              <h2 class="text-lg">Still not answered?</h2>
              <p class="mt-1 text-sm">
                The Help button in the corner reaches the team directly. You do not need an account.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </AppLayout>
</template>
